/**
 * Sale countdown.
 *
 * The end time comes from a time-limited Item Badge's `valid_upto`, which
 * get_item_badges already returns and already filters to active rows — no new
 * field, endpoint or round-trip (see adapters/webshop.js adaptCountdown).
 *
 * ONE interval for the whole page, not one per card. A listing can hold dozens
 * of cards, and dozens of independent timers would each schedule their own
 * layout work; a single tick updates them all in one pass and stops itself the
 * moment nothing on the page still needs it.
 *
 * The ticker only writes text into nodes the RENDERER produced. It never builds
 * or removes markup — that stays with the layouts.
 */

import { el, svg } from '../dom.js';

const CLOCK = '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>';
const SELECTOR = '[data-rfhq-countdown]';

/** The ribbon itself. Layouts call this; it renders nothing without an end. */
export function renderCountdown(model) {
	const endsAt = model.countdown && model.countdown.endsAt;
	if (!endsAt) return null;

	const remaining = format(msUntil(endsAt));
	// Already over by the time we render — show nothing rather than 00:00:00.
	if (!remaining) return null;

	return el('div', { class: 'rfhq-pc-countdown', dataset: { rfhqCountdown: endsAt } }, [
		svg(CLOCK, { class: 'rfhq-pc-countdown__icon' }),
		el('span', { class: 'rfhq-pc-countdown__label', text: 'Ends in' }),
		el('span', { class: 'rfhq-pc-countdown__digits', text: remaining }),
	]);
}

function msUntil(endsAt) {
	const end = Date.parse(endsAt);
	if (!isFinite(end)) return 0;
	return end - Date.now();
}

/** `2d 04:15` past a day, `04:15:09` under one. Empty string once elapsed. */
export function format(ms) {
	if (!isFinite(ms) || ms <= 0) return '';

	const totalSeconds = Math.floor(ms / 1000);
	const days = Math.floor(totalSeconds / 86400);
	const hours = Math.floor((totalSeconds % 86400) / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const pad = (n) => String(n).padStart(2, '0');
	if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}`;
	return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/* ── the single page-wide ticker ─────────────────────────────────────────── */

let timer = null;

function tick() {
	if (typeof document === 'undefined') return;

	const nodes = document.querySelectorAll(SELECTOR);
	if (!nodes.length) {
		stopCountdownTicker();
		return;
	}

	for (const node of nodes) {
		const remaining = format(msUntil(node.dataset.rfhqCountdown));
		if (!remaining) {
			// Elapsed while the shopper was on the page. Remove the ribbon
			// rather than freeze it at zero — a finished sale should stop
			// claiming urgency.
			node.remove();
			continue;
		}
		const digits = node.querySelector('.rfhq-pc-countdown__digits');
		if (digits && digits.textContent !== remaining) digits.textContent = remaining;
	}
}

/** Idempotent: safe to call after every append. */
export function startCountdownTicker() {
	if (timer || typeof document === 'undefined') return;
	if (!document.querySelector(SELECTOR)) return; // nothing to count yet
	timer = setInterval(tick, 1000);
}

export function stopCountdownTicker() {
	if (!timer) return;
	clearInterval(timer);
	timer = null;
}
